"use client";

/**
 * Declarative kebab (three-dot) menu component
 * Owns open/close state, outside-click dismissal, keyboard navigation, and animated container
 * Renders menu items from a groups array with automatic separators between groups
 * Uses fixed positioning to escape overflow containers (e.g. scrollable sidebars)
 */

import type React from "react";
import { forwardRef, useCallback, useId, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useClickOutside } from "../../_hooks/useClickOutside";
import { useMenuKeyboardNav } from "../../_hooks/useMenuKeyboardNav";
import { OptionsMenuButton } from "./OptionsMenuButton";
import { MENU_PANEL_CLASS, MENU_SEPARATOR_CLASS } from "@/fetch-ui/_logic/menu-styles";
import { cn } from "@/lib/utils/index";
import { DESTRUCTIVE_MENU_ITEM_CLASS, DISABLED_MENU_ITEM_CLASS, MENU_ITEM_CLASS } from "./menu-styles";
import type { MenuGroup } from "./menu-types";

/** Imperative handle for opening/closing the menu programmatically (e.g. from right-click or drag start) */
export interface KebabMenuHandle {
  open: (cursorPosition?: { x: number; y: number }) => void;
  close: () => void;
}

interface KebabMenuProps {
  /** Groups of menu items — a separator renders between each visible group */
  groups: MenuGroup[];
  /** Accessible label for the trigger button (e.g., "Question options") */
  triggerLabel: string;
  /** Tailwind width class for the dropdown panel (default "min-w-44 w-max" — grows to fit the longest label) */
  menuWidth?: string;
  /** Optional override for the trigger button's color/style (e.g. white icon over a photo header) */
  triggerClassName?: string;
}

/** Estimated menu height (px) used to decide if the dropdown should flip upward */
const MENU_HEIGHT_ESTIMATE = 200;

/** Gap (px) between the trigger button and the dropdown panel */
const MENU_GAP = 4;

/**
 * Finds the nearest scrollable ancestor element
 * @param element - Starting element to search from
 * @returns The nearest scrollable ancestor or null if none found
 */
function findScrollParent(element: Element): Element | null {
  let parent: Element | null = element.parentElement;
  while (parent) {
    const { overflowY } = getComputedStyle(parent);
    if (overflowY === "auto" || overflowY === "scroll") return parent;
    parent = parent.parentElement;
  }
  return null;
}

/**
 * Kebab menu with declarative item groups, animated dropdown, and keyboard navigation
 * Supports disabled items (visible but grayed out) and hidden items (not rendered)
 * Exposes an imperative `open()` handle for right-click context menu support
 */
export const KebabMenu = forwardRef<KebabMenuHandle, KebabMenuProps>(function KebabMenu(
  { groups, triggerLabel, menuWidth = "min-w-44 w-max", triggerClassName },
  ref
): React.ReactElement {
  // IDs
  const menuId = useId();

  // States
  const [isOpen, setIsOpen] = useState(false);
  const [placement, setPlacement] = useState<"bottom" | "top">("bottom");
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

  // Refs
  const menuRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const scrollParentRef = useRef<Element | null>(null);
  const hasRepositionedRef = useRef(false);

  // Hooks
  const closeMenu = useCallback(() => setIsOpen(false), []);
  useClickOutside(menuRef, isOpen, closeMenu);
  const { menuContentRef, handleMenuKeyDown } = useMenuKeyboardNav({ isOpen, onClose: closeMenu });

  // Derived values - filter to groups with at least one visible item. Only needed while the menu
  // is actually rendered, since a closed menu's groups aren't shown.
  const visibleGroups = useMemo(
    () =>
      isOpen ? groups.map((group) => group.filter((item) => !item.hidden)).filter((group) => group.length > 0) : [],
    [isOpen, groups]
  );

  /**
   * Computes fixed position coordinates from the trigger wrapper's bounding rect.
   * Initially positions below, then adjusts after render if the menu overflows.
   */
  const computePosition = useCallback(() => {
    if (!menuRef.current) return;
    const rect = menuRef.current.getBoundingClientRect();

    scrollParentRef.current = findScrollParent(menuRef.current);
    const scrollParent = scrollParentRef.current;
    const containerBottom = scrollParent ? scrollParent.getBoundingClientRect().bottom : window.innerHeight;
    const spaceBelow = containerBottom - rect.bottom;
    const opensUp = spaceBelow < MENU_HEIGHT_ESTIMATE;

    setPlacement(opensUp ? "top" : "bottom");
    setMenuStyle({
      position: "fixed",
      right: window.innerWidth - rect.right,
      ...(opensUp ? { bottom: window.innerHeight - rect.top + MENU_GAP } : { top: rect.bottom + MENU_GAP }),
    });
  }, []);

  // Handlers
  const openMenu = useCallback(() => {
    hasRepositionedRef.current = false;
    computePosition();
    setIsOpen(true);
  }, [computePosition]);

  const handleItemClick = useCallback((e: React.MouseEvent, onClick: () => void) => {
    e.stopPropagation();
    onClick();
    setIsOpen(false);
  }, []);

  // Effects — reposition after render using actual menu height (runs once per open)
  useLayoutEffect(() => {
    if (!isOpen || hasRepositionedRef.current || !dropdownRef.current || !menuRef.current) return;

    const dropdownRect = dropdownRef.current.getBoundingClientRect();
    const triggerRect = menuRef.current.getBoundingClientRect();
    const scrollParent = scrollParentRef.current;
    const containerBottom = scrollParent ? scrollParent.getBoundingClientRect().bottom : window.innerHeight;
    const containerTop = scrollParent ? scrollParent.getBoundingClientRect().top : 0;

    // Check if menu overflows below the container
    if (placement === "bottom" && dropdownRect.bottom > containerBottom) {
      const spaceAbove = triggerRect.top - containerTop;
      if (spaceAbove > dropdownRect.height + MENU_GAP) {
        hasRepositionedRef.current = true;
        setPlacement("top");
        setMenuStyle((prev) => ({
          ...prev,
          top: undefined,
          bottom: window.innerHeight - triggerRect.top + MENU_GAP,
        }));
        return;
      }
    }

    // Check if menu overflows above the container
    if (placement === "top" && dropdownRect.top < containerTop) {
      const spaceBelow = containerBottom - triggerRect.bottom;
      if (spaceBelow > dropdownRect.height + MENU_GAP) {
        hasRepositionedRef.current = true;
        setPlacement("bottom");
        setMenuStyle((prev) => ({
          ...prev,
          bottom: undefined,
          top: triggerRect.bottom + MENU_GAP,
        }));
      }
    }
  }, [isOpen, placement]);

  // Effects — close on scroll since fixed position won't follow the trigger
  useLayoutEffect(() => {
    if (!isOpen) return;
    const scrollParent = scrollParentRef.current;
    if (scrollParent) {
      const handleScroll = () => setIsOpen(false);
      scrollParent.addEventListener("scroll", handleScroll, { passive: true });
      return () => scrollParent.removeEventListener("scroll", handleScroll);
    }
  }, [isOpen]);

  // Imperative handle for right-click support
  useImperativeHandle(
    ref,
    () => ({
      open: (cursorPosition?: { x: number; y: number }) => {
        if (cursorPosition) {
          const spaceBelow = window.innerHeight - cursorPosition.y;
          const opensUp = spaceBelow < MENU_HEIGHT_ESTIMATE;
          setPlacement(opensUp ? "top" : "bottom");
          setMenuStyle({
            position: "fixed",
            left: cursorPosition.x,
            ...(opensUp
              ? { bottom: window.innerHeight - cursorPosition.y + MENU_GAP }
              : { top: cursorPosition.y + MENU_GAP }),
          });
        } else {
          computePosition();
        }
        setIsOpen(true);
      },
      close: () => setIsOpen(false),
    }),
    [computePosition]
  );

  // Render
  return (
    <div className="relative" ref={menuRef}>
      <OptionsMenuButton
        onClick={() => (isOpen ? setIsOpen(false) : openMenu())}
        ariaLabel={triggerLabel}
        ariaExpanded={isOpen}
        ariaControls={menuId}
        className={triggerClassName}
      />
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={dropdownRef}
            initial={{ opacity: 0, scale: 0.95, y: placement === "bottom" ? -4 : 4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: placement === "bottom" ? -4 : 4 }}
            transition={{ duration: 0.15 }}
            style={menuStyle}
            className={cn("z-50", menuWidth, MENU_PANEL_CLASS)}
            role="menu"
            id={menuId}
          >
            <div ref={menuContentRef} role="presentation" onKeyDown={handleMenuKeyDown}>
              {visibleGroups.map((group, groupIndex) => (
                <div key={groupIndex}>
                  {groupIndex > 0 && <div role="separator" className={MENU_SEPARATOR_CLASS} />}
                  {group.map((item) => {
                    const className = item.disabled
                      ? DISABLED_MENU_ITEM_CLASS
                      : item.variant === "destructive"
                        ? DESTRUCTIVE_MENU_ITEM_CLASS
                        : MENU_ITEM_CLASS;

                    return (
                      <button
                        key={item.label}
                        type="button"
                        role="menuitem"
                        onClick={(e) => handleItemClick(e, item.onClick)}
                        disabled={item.disabled}
                        className={className}
                      >
                        <item.icon className="h-4 w-4 shrink-0" />
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});
